import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import API from '../services/api';
import {
  Truck, Search, RefreshCw, Copy, Check, ExternalLink, NotebookPen,
  X, AlertTriangle, ChevronLeft, ChevronRight,
} from 'lucide-react';

const STATUSES = ['Bestellt', 'Versendet', 'In Zustellung', 'Zugestellt', 'Storniert'];
const STATUS_STYLE = {
  Bestellt: { color: '#fbbf24', bg: 'rgba(251,191,36,0.14)' },
  Versendet: { color: '#60a5fa', bg: 'rgba(96,165,250,0.14)' },
  'In Zustellung': { color: '#c084fc', bg: 'rgba(192,132,252,0.14)' },
  Zugestellt: { color: '#4ade80', bg: 'rgba(74,222,128,0.14)' },
  Storniert: { color: '#f87171', bg: 'rgba(248,113,113,0.14)' },
};

function money(value) {
  if (value === null || value === undefined || value === '') return '—';
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return number.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function date(value, withTime = false) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('de-DE', withTime
    ? { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }
    : { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function InlineMoney({ value, onSave, title }) {
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(value ?? ''), [value]);

  const save = async () => {
    if (saving) return;
    const normalized = draft === '' ? null : Number(String(draft).replace(',', '.'));
    if (normalized !== null && !Number.isFinite(normalized)) {
      setDraft(value ?? '');
      return;
    }

    const current = value === null || value === undefined || value === '' ? null : Number(value);
    if (normalized === current) return;

    setSaving(true);
    try {
      await onSave(normalized);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      title={`${title} eingeben`}
      style={{
        width: 118,
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        border: '1px solid var(--border)',
        background: 'var(--bg2)',
        borderRadius: 7,
        padding: '2px 7px',
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.02)',
        opacity: saving ? 0.65 : 1,
      }}
    >
      <input
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0"
        value={draft}
        disabled={saving}
        aria-label={title}
        placeholder="Betrag"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={save}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
          if (event.key === 'Escape') {
            setDraft(value ?? '');
            event.currentTarget.blur();
          }
        }}
        style={{
          width: 82,
          minWidth: 0,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          color: 'var(--text)',
          fontFamily: 'DM Mono',
          fontSize: 12,
          padding: '5px 0',
        }}
      />
      <span style={{ color: 'var(--text3)', fontSize: 12 }}>€</span>
    </div>
  );
}

function StatCard({ label, value, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ minWidth: 130, textAlign: 'left', background: active ? 'rgba(124,58,237,0.16)' : 'var(--bg2)', border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 10, padding: '12px 14px', cursor: 'pointer', color: 'var(--text)' }}
    >
      <div style={{ fontSize: 20, fontWeight: 800, fontFamily: 'Syne' }}>{Number(value || 0).toLocaleString('de-DE')}</div>
      <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 2 }}>{label}</div>
    </button>
  );
}

export default function DeliveryStatus() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [city, setCity] = useState('');
  const [cities, setCities] = useState([]);
  const [canViewAllCities, setCanViewAllCities] = useState(false);
  const [page, setPage] = useState(1);
  const [importing, setImporting] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [noteOrder, setNoteOrder] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const limit = 100;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await API.get('/api/delivery-status', {
        params: { search, status, city, page, limit },
      });
      setOrders(response.data.orders || []);
      setStats(response.data.stats || {});
      setCities(response.data.cities || []);
      setCanViewAllCities(Boolean(response.data.canViewAllCities));
      setTotal(response.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Delivery-Daten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [search, status, city, page]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const updateLocal = useCallback((id, patch) => {
    setOrders((current) => current.map((order) => Number(order.id) === Number(id) ? { ...order, ...patch } : order));
  }, []);

  const saveField = useCallback(async (order, field, value) => {
    const payload = { [field]: value };
    const response = await API.patch(`/api/delivery-status/${order.id}`, payload);
    const updated = response.data.order || {};
    const patch = {
      delivery_updated_at: updated.delivery_updated_at ?? new Date().toISOString(),
    };

    if (field === 'price') patch.price = updated.price ?? value;
    if (field === 'soldFor') patch.delivery_sold_for = updated.delivery_sold_for ?? value;
    if (field === 'notes') patch.delivery_notes = updated.delivery_notes ?? value;
    if (field === 'status') patch.delivery_status = updated.delivery_status ?? value;
    if (field === 'city') patch.delivery_city = updated.delivery_city ?? value;

    updateLocal(order.id, patch);
  }, [updateLocal]);

  const triggerImport = async () => {
    setImporting(true);
    setError('');
    try {
      await API.post('/api/delivery-status/import');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Mailimport fehlgeschlagen.');
    } finally {
      setImporting(false);
    }
  };

  const copyOtp = async (order) => {
    if (!order.otp_code) return;
    try {
      await navigator.clipboard.writeText(order.otp_code);
      setCopiedId(order.id);
      setTimeout(() => setCopiedId(null), 1600);
    } catch {
      setError('OTP konnte nicht kopiert werden.');
    }
  };

  const openNote = (order) => {
    setNoteOrder(order);
    setNoteText(order.delivery_notes || '');
  };

  const saveNote = async () => {
    if (!noteOrder) return;
    setSavingNote(true);
    try {
      await saveField(noteOrder, 'notes', noteText);
      setNoteOrder(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Notiz konnte nicht gespeichert werden.');
    } finally {
      setSavingNote(false);
    }
  };

  const statCards = useMemo(() => STATUSES.map((item) => ({ label: item, value: stats[item] || 0 })), [stats]);

  return (
    <div style={{ padding: 24, minHeight: '100vh', background: 'var(--bg)' }}>
      <style>{`
        .delivery-table { width: 100%; border-collapse: collapse; min-width: 1260px; }
        .delivery-table th { text-align: left; padding: 11px 12px; color: var(--text3); font-size: 11px; font-weight: 700; border-bottom: 1px solid var(--border); background: var(--bg2); position: sticky; top: 0; z-index: 1; }
        .delivery-table td { padding: 10px 12px; border-bottom: 1px solid var(--border); font-size: 12px; vertical-align: middle; }
        .delivery-table tr:hover td { background: rgba(124,58,237,0.035); }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @media (max-width: 768px) { .delivery-page-header { padding-left: 44px; } }
      `}</style>

      <div className="delivery-page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Truck size={24} color="var(--accent)" />
            <h1 style={{ margin: 0, fontFamily: 'Syne', fontSize: 24 }}>Delivery Status</h1>
          </div>
          <div style={{ color: 'var(--text3)', fontSize: 12, marginTop: 5 }}>{total.toLocaleString('de-DE')} Bestellungen</div>
        </div>
        <button type="button" className="btn btn-primary" onClick={triggerImport} disabled={importing} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <RefreshCw size={14} style={{ animation: importing ? 'spin 0.8s linear infinite' : 'none' }} />
          {importing ? 'Import läuft...' : 'Mails jetzt prüfen'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
        <StatCard label="Alle" value={Object.values(stats).reduce((sum, value) => sum + Number(value || 0), 0)} active={!status} onClick={() => { setStatus(''); setPage(1); }} />
        {statCards.map((card) => (
          <StatCard key={card.label} {...card} active={status === card.label} onClick={() => { setStatus(status === card.label ? '' : card.label); setPage(1); }} />
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 340px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 9, padding: '0 11px' }}>
          <Search size={15} color="var(--text3)" />
          <input
            value={search}
            onChange={(event) => { setSearch(event.target.value); setPage(1); }}
            placeholder="Produkt, Bestellnummer, ASIN oder Ort suchen..."
            style={{ border: 'none', background: 'transparent', width: '100%', padding: '10px 0', outline: 'none' }}
          />
          {search && <button type="button" onClick={() => setSearch('')} style={{ border: 'none', background: 'transparent', color: 'var(--text3)', cursor: 'pointer' }}><X size={14} /></button>}
        </div>
        <select value={city} onChange={(event) => { setCity(event.target.value); setPage(1); }} style={{ minWidth: 190 }}>
          <option value="">{canViewAllCities ? 'Alle Orte' : 'Alle freigegebenen Orte'}</option>
          {cities.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
        <select value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }} style={{ minWidth: 170 }}>
          <option value="">Alle Status</option>
          {STATUSES.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
      </div>

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 13px', marginBottom: 14, borderRadius: 8, color: '#fca5a5', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'auto' }}>
        <table className="delivery-table">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Produkt</th>
              <th>Bestellnummer</th>
              <th>Preis</th>
              <th>Ort</th>
              <th>Voraussichtliche Lieferung</th>
              <th>OTP</th>
              <th>Delivery-Status</th>
              <th>Verkauft für</th>
              <th>Notiz</th>
              <th>Letztes Update</th>
            </tr>
          </thead>
          <tbody>
            {!loading && orders.map((order) => {
              const badge = STATUS_STYLE[order.delivery_status] || STATUS_STYLE.Bestellt;
              const amazonUrl = order.asin ? `https://www.amazon.de/dp/${order.asin}` : null;
              return (
                <tr key={order.id}>
                  <td style={{ whiteSpace: 'nowrap', fontFamily: 'DM Mono' }}>{date(order.order_date || order.order_time)}</td>
                  <td style={{ minWidth: 270, maxWidth: 420 }}>
                    {amazonUrl ? (
                      <a href={amazonUrl} target="_blank" rel="noreferrer" title={order.product_name || ''} style={{ color: 'var(--text)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.product_name || order.asin}</span>
                        <ExternalLink size={12} color="var(--accent)" style={{ flexShrink: 0 }} />
                      </a>
                    ) : (
                      <span title={order.product_name || ''} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.product_name || '—'}</span>
                    )}
                  </td>
                  <td style={{ fontFamily: 'DM Mono', whiteSpace: 'nowrap' }}>{order.order_number}</td>
                  <td><InlineMoney value={order.price} title="Preis" onSave={async (value) => { try { await saveField(order, 'price', value); } catch (err) { setError(err.response?.data?.error || 'Preis konnte nicht gespeichert werden.'); await load(); } }} /></td>
                  <td>{order.delivery_city || '—'}</td>
                  <td style={{ minWidth: 210, color: order.delivery_eta_text ? 'var(--text)' : 'var(--text3)' }}>
                    {order.delivery_eta_text || '—'}
                  </td>
                  <td style={{ minWidth: 118 }}>
                    {order.otp_code ? (
                      <button type="button" onClick={() => copyOtp(order)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid rgba(192,132,252,0.45)', background: 'rgba(192,132,252,0.12)', color: '#d8b4fe', borderRadius: 7, padding: '5px 8px', cursor: 'pointer', fontFamily: 'DM Mono', fontWeight: 700 }}>
                        {order.otp_code} {copiedId === order.id ? <Check size={12} /> : <Copy size={12} />}
                      </button>
                    ) : order.otp_expired ? (
                      <span style={{ color: 'var(--text3)', fontSize: 10 }}>abgelaufen</span>
                    ) : (
                      <span style={{ color: 'var(--text3)' }}>—</span>
                    )}
                  </td>
                  <td>
                    <select
                      value={order.delivery_status || 'Bestellt'}
                      onChange={async (event) => {
                        const value = event.target.value;
                        updateLocal(order.id, { delivery_status: value });
                        try { await saveField(order, 'status', value); } catch { await load(); }
                      }}
                      style={{ minWidth: 135, color: badge.color, background: badge.bg, borderColor: badge.color, fontWeight: 700, fontSize: 11 }}
                    >
                      {STATUSES.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </td>
                  <td><InlineMoney value={order.delivery_sold_for} title="Verkauft für" onSave={async (value) => { try { await saveField(order, 'soldFor', value); } catch (err) { setError(err.response?.data?.error || 'Verkaufspreis konnte nicht gespeichert werden.'); await load(); } }} /></td>
                  <td>
                    <button type="button" onClick={() => openNote(order)} title={order.delivery_notes || 'Notiz hinzufügen'} style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--border)', background: order.delivery_notes ? 'rgba(59,130,246,0.10)' : 'transparent', color: order.delivery_notes ? '#93c5fd' : 'var(--text3)', borderRadius: 7, padding: '5px 8px', cursor: 'pointer', maxWidth: 170 }}>
                      <NotebookPen size={13} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.delivery_notes || 'Notiz'}</span>
                    </button>
                  </td>
                  <td style={{ whiteSpace: 'nowrap', color: 'var(--text3)', fontFamily: 'DM Mono', fontSize: 11 }}>{date(order.delivery_updated_at, true)}</td>
                </tr>
              );
            })}
            {!loading && orders.length === 0 && (
              <tr><td colSpan="11" style={{ padding: 34, textAlign: 'center', color: 'var(--text3)' }}>Keine passenden Bestellungen gefunden.</td></tr>
            )}
            {loading && (
              <tr><td colSpan="11" style={{ padding: 34, textAlign: 'center', color: 'var(--text3)' }}>Lädt...</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, color: 'var(--text3)', fontSize: 11 }}>
        <span>Seite {page} von {totalPages}</span>
        <div style={{ display: 'flex', gap: 7 }}>
          <button type="button" className="btn btn-ghost" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}><ChevronLeft size={14} /></button>
          <button type="button" className="btn btn-ghost" disabled={page >= totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}><ChevronRight size={14} /></button>
        </div>
      </div>

      {noteOrder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }} onMouseDown={(event) => { if (event.target === event.currentTarget) setNoteOrder(null); }}>
          <div style={{ width: 'min(560px, 100%)', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
              <div>
                <h3 style={{ margin: 0, fontFamily: 'Syne' }}>Artikelnotiz</h3>
                <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 3 }}>{noteOrder.order_number}</div>
              </div>
              <button type="button" onClick={() => setNoteOrder(null)} style={{ border: 'none', background: 'transparent', color: 'var(--text3)', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <textarea value={noteText} onChange={(event) => setNoteText(event.target.value)} rows={8} maxLength={5000} placeholder="Notiz zum Artikel..." style={{ width: '100%', resize: 'vertical' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 13 }}>
              <button type="button" className="btn btn-ghost" onClick={() => setNoteOrder(null)}>Abbrechen</button>
              <button type="button" className="btn btn-primary" disabled={savingNote} onClick={saveNote}>{savingNote ? 'Speichert...' : 'Speichern'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
